import { Injectable, NgZone, OnDestroy, signal } from '@angular/core';
import { onAuthStateChanged, Unsubscribe } from '@firebase/auth';
import { doc, onSnapshot } from '@firebase/firestore';
import { getFunctions, httpsCallable } from '@firebase/functions';
import { AppService } from '../app.service';

export interface LegalDocuments {
  version: string;
  title: string;
  termsUrl: string;
  privacyUrl: string;
}

interface LegalStatus {
  documents: LegalDocuments | null;
  accepted: boolean;
  isAdmin: boolean;
}

@Injectable({ providedIn: 'root' })
export class LegalAgreementsService implements OnDestroy {
  readonly documents = signal<LegalDocuments | null>(null);
  readonly allowed = signal(false);
  readonly loading = signal(true);
  readonly isAdmin = signal(false);
  readonly error = signal('');
  private readonly functions;
  private readonly stopAuth: Unsubscribe;
  private stopDocuments?: Unsubscribe;
  private generation = 0;

  constructor(private app: AppService, private zone: NgZone) {
    this.functions = getFunctions(app.firebaseApp, 'europe-west2');
    this.stopAuth = onAuthStateChanged(app.firebaseAuth, user => {
      this.stopDocuments?.();
      this.stopDocuments = undefined;
      this.generation++;
      this.zone.run(() => {
        this.allowed.set(false);
        this.documents.set(null);
        this.isAdmin.set(false);
        this.error.set('');
        this.loading.set(!!user);
      });
      if (user) {
        this.listenForDocuments();
        void this.refresh();
      }
    });
  }

  private listenForDocuments(): void {
    this.stopDocuments = onSnapshot(doc(this.app.firestore, 'legalConfig', 'current'),
      { includeMetadataChanges: true },
      snapshot => this.zone.run(() => {
        // Cached documents cannot establish the currently published version.
        if (!snapshot.metadata.fromCache) {
          if (snapshot.get('version') !== this.documents()?.version) {
            this.allowed.set(false);
          }
          void this.refresh(false);
        }
      }),
      error => this.zone.run(() => {
        this.stopDocuments = undefined;
        this.generation++;
        this.allowed.set(false);
        this.loading.set(false);
        this.error.set('Unable to check the legal documents. Retry or sign out.');
        console.error('Legal document subscription failed:', error);
      }));
  }

  async refresh(revokeAccess = true): Promise<void> {
    if (this.app.firebaseAuth.currentUser && !this.stopDocuments) {
      this.listenForDocuments();
    }
    const generation = ++this.generation;
    const uid = this.app.firebaseAuth.currentUser?.uid;
    if (revokeAccess) {
      this.allowed.set(false);
    }
    this.loading.set(true);
    this.error.set('');
    try {
      const response = await httpsCallable<void, LegalStatus>(this.functions, 'getLegalStatus')();
      if (generation !== this.generation || uid !== this.app.firebaseAuth.currentUser?.uid) {
        return;
      }
      this.zone.run(() => {
        this.documents.set(response.data.documents);
        this.isAdmin.set(response.data.isAdmin);
        this.allowed.set(response.data.accepted);
        this.loading.set(false);
      });
    } catch (error) {
      if (generation !== this.generation) {
        return;
      }
      this.zone.run(() => {
        this.allowed.set(false);
        this.loading.set(false);
        this.error.set('Unable to verify your agreement. An internet connection is required. Please retry.');
      });
      console.error('Legal agreement verification failed:', error);
    }
  }

  async accept(version: string): Promise<void> {
    await httpsCallable<{ version: string }, { accepted: boolean }>(
      this.functions, 'acceptLegalAgreements')({ version });
    await this.refresh();
  }

  async publish(title: string, terms: File, privacy: File): Promise<void> {
    const expectedVersion = this.documents()?.version ?? null;
    const [termsData, privacyData] = await Promise.all([this.readPdf(terms), this.readPdf(privacy)]);
    await httpsCallable(this.functions, 'publishLegalAgreements')({
      title, terms: termsData, privacy: privacyData, expectedVersion
    });
    await this.refresh();
  }

  private async readPdf(file: File): Promise<string> {
    if (file.size > 3 * 1024 * 1024) {
      throw new Error('Each PDF must be no larger than 3 MB.');
    }
    const bytes = new Uint8Array(await file.arrayBuffer());
    if (new TextDecoder().decode(bytes.slice(0, 5)) !== '%PDF-') {
      throw new Error('Please select valid PDF files exported from Word.');
    }
    let binary = '';
    for (let offset = 0; offset < bytes.length; offset += 8192) {
      binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
    }
    return btoa(binary);
  }

  ngOnDestroy(): void {
    this.generation++;
    this.stopDocuments?.();
    this.stopAuth();
  }
}
